import { ALL_GAMES, roundDifficulty, roundLength, whenItWas, type PlayedRound } from '@hazel/shared'

import { Card } from '../components/Card.js'

/** The teddy whose game this was, for the line that names it. */
function gameName(gameKey: string): string {
  return ALL_GAMES.find((game) => game.key === gameKey)?.title ?? gameKey
}

/**
 * One round, in a sentence.
 *
 * How it went comes first and is said in the round's own terms — "you got
 * MONDAY" — because that is the bit worth reading. The difficulty and the
 * length follow quietly. No marks, no comparison, nothing that turns a round
 * into a score.
 */
function tellIt(round: PlayedRound): string {
  const name = gameName(round.gameKey)

  if (round.result === 'won') {
    return round.detail ? `You got ${round.detail}` : `You finished ${name}`
  }

  // Lost and abandoned are told apart, and neither is told as a failure.
  //
  // They are genuinely different things: a lost round is one she played to the
  // end and the word beat her, and an abandoned one is a round she walked away
  // from — a scramble left unsolved files as abandoned, not lost. Narrating
  // that as "you tried and failed" invents a defeat she never had.
  //
  // Meeting a word she does not know yet is not a failure either way. Now she
  // knows it, which is the whole point of the game.
  if (round.result === 'lost') {
    return round.detail ? `The word was ${round.detail}` : `${name} won that one`
  }

  return round.detail ? `You started ${round.detail}` : `You had a go at ${name}`
}

export function RecentRounds({
  rounds,
  daysPlayed,
}: {
  rounds: readonly PlayedRound[]
  daysPlayed: number
}) {
  if (rounds.length === 0) return null

  return (
    <Card>
      <h2 className="text-(length:--text-heading) mb-1">Lately</h2>

      {/* Days, not a streak. A streak turns a missed day into a loss, and the
          day she most needs this to be friendly is the one after a week off. */}
      <p className="text-ink-muted text-(length:--text-small) mb-3">
        {daysPlayed === 1
          ? 'You have played on one day so far.'
          : `You have played on ${daysPlayed} different days.`}
      </p>

      <ul className="flex list-none flex-col gap-2 p-0">
        {rounds.map((round) => (
          <li key={round.id} className="flex flex-wrap items-baseline justify-between gap-x-3">
            <span>{tellIt(round)}</span>
            <span className="text-ink-muted text-(length:--text-small)">
              {[
                gameName(round.gameKey),
                roundDifficulty(round),
                roundLength(round.durationMs),
                whenItWas(round.playedAt),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
